import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { AutomationMethods } from "enums/Methods";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateBarcodeField } from "features/WorklistTools/shared/components/PlateBarcodeField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import { SelectedSystem } from "features/WorklistTools/shared/components/SelectedSystem";
import {
  DeadTotalCountSpinOrder as DeadTotalCountSpinOrderEnum,
  PlatePosition,
} from "features/WorklistTools/shared/interfaces";
import { is384W_CCU_Plate } from "features/WorklistTools/shared/PlateHelpers";
import {
  getLargestPlateBarcodeSeries,
  getUniqueDestinationPlateBarcode,
  gForceToRpm,
  rpmToGForce,
} from "features/WorklistTools/shared/WorklistHelpers";
import { useGetIntermediateDeadAndDestPlateBarcodesMutation } from "graphql/generated/schema-types";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import { useEffect } from "react";
import styled from "styled-components";
import { DestPlateStringKeys, Twelve24TCToolActions } from "../../state";
import { useHandleMethodSettingsUpdated } from "../../state/handlers";
import { useTwelve24TCStateProvider } from "../../state/StateProvider";
import { AliquotSettings } from "./components/AliquotSettings";
import { DeadTotalCountSpinOrder } from "./components/DeadTotalCountSpinOrder";
import {
  DefaultVSpinSettings,
  DefaultVSpinSettingsDict,
} from "./components/DefaultVSpinSettings";
import { DissociationWashReagent } from "./components/DissociationWashReagent";
import { PelletResuspensionVolume } from "./components/PelletResuspensionVolume";
import { PrePelletSplitTimer } from "./components/PrePelletSplitTimer";
import { SelectPlateCode } from "./components/SelectPlateCode";

const StyledMethodSettingsContainer = styled.div`
  height: 500px;
  overflow: auto;
`;

const StyledMethodSettingsContentContainer = styled.div`
  display: grid;
  grid-template: auto / 1fr;
  grid-gap: 15px;
  margin-top: 5px;
  margin-right: 15px;
`;

export function MethodSettings({ deadPlateTypeInfo }: any) {
  const { twelve24TCToolInternalState } = useTwelve24TCStateProvider();
  const methodSettings = twelve24TCToolInternalState.methodSettings;
  const twelve24TCState = useAppReduxSelector(
    (state) => state.WorklistTools.Twelve24TCTool.present,
  );
  const dispatch = useAppReduxDispatch();
  const updateMethodSettings = useHandleMethodSettingsUpdated();

  const handleMethodSettingsChanged = async (key: string, value: any) => {
    switch (key) {
      case "spinParamGForce":
        const spinRPM = gForceToRpm(value);
        updateMethodSettings("spinParamPercent", spinRPM);
        break;
      case "spinParamPercent":
        const GForceValue = rpmToGForce(value);
        updateMethodSettings("spinParamGForce", GForceValue);
        break;
      case "dissociation":
        updateMethodSettings("dissociation", value);
        updateMethodSettings("dissociationTime", 0);
        break;
      case "defaultVSpinSettings":
        handleDefaultVSpinSettings(value);
        break;
    }
    updateMethodSettings(key, value);
  };

  const handleDefaultVSpinSettings = (value: boolean) => {
    if (value) {
      DefaultVSpinSettingsDict.forEach((i, val) => {
        updateMethodSettings(val, DefaultVSpinSettingsDict.get(val));
      });
    } else {
      DefaultVSpinSettingsDict.forEach((i, val) => {
        updateMethodSettings(val, 0);
      });
    }
  };

  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.Twelve24TCTool.present.sourcePlateInfo,
  );

  const destPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.Twelve24TCTool.present.destPlateInfo,
  );

  const deadPlateType = useAppReduxSelector(
    (state) => state.WorklistTools.Twelve24TCTool.present.deadPlateType,
  );

  const handleDestPlateSettingsChanged = (
    index: number,
    key: DestPlateStringKeys,
    value: string | number | boolean,
  ) => {
    dispatch(
      Twelve24TCToolActions.UPDATE_DESTINATION_PLATE_INFO({
        key,
        value,
      }),
    );
  };

  const [getAssociatedBarcodes] =
    useGetIntermediateDeadAndDestPlateBarcodesMutation();

  const getAndSetBarcodes = (plateCode: string) => {
    const hasSourceBarcode = sourcePlateInfo.some(
      (plate) => plate.plateBarcode,
    );

    if (!hasSourceBarcode) {
      handleDestPlateSettingsChanged(0, "plateCode", plateCode);
      return;
    }

    getAssociatedBarcodes({
      variables: {
        sourcePlateBarcode: getLargestPlateBarcodeSeries(sourcePlateInfo),
        destPlateCode: plateCode,
        hasMultipleSourcePlateTypes: false,
        hasMultipleDestPlateTypes: false,
        has384DeadPlate: is384W_CCU_Plate(deadPlateType),
      },
    }).then((data: any) => {
      const destPlate = getUniqueDestinationPlateBarcode(
        data.data.intermediateDeadAndDestPlateBarcodes.destinationPlateBarcode,
        [destPlateInfo],
      );

      handleDestPlateSettingsChanged(0, "plateBarcode", destPlate);
    });
  };

  const handleDeadPlateBarcodeChange = (value: string) => {
    dispatch(
      Twelve24TCToolActions.UPDATE_DEAD_PLATEBARCODE({
        value,
      }),
    );
  };

  const handleDeadPlateTypeChange = (value: string) => {
    dispatch(
      Twelve24TCToolActions.UPDATE_DEAD_PLATETYPE({
        value,
      }),
    );
  };

  useEffect(() => {
    if (methodSettings.pelletResuspensionVol === 0) {
      updateMethodSettings(
        "deadTotalCountSpinOrder",
        DeadTotalCountSpinOrderEnum.CountBeforeSpin,
      );
    }
  }, [methodSettings.pelletResuspensionVol]);

  return (
    <StyledMethodSettingsContainer>
      <StyledMethodSettingsContentContainer>
        <SelectedSystem
          automationMethodId={AutomationMethods.Twelve24TC}
          selectedSystem={methodSettings.selectedSystem}
          handleSystemChanged={({ systemId, asamId }) => {
            updateMethodSettings("arraySystemAutomationMethodId", asamId);
            updateMethodSettings("selectedSystem", systemId);
          }}
        />

        <PelletResuspensionVolume
          twelve24TCInternalState={twelve24TCToolInternalState}
          handleMethodSettingsChanged={handleMethodSettingsChanged}
        />
        <PrePelletSplitTimer
          twelve24TCInternalState={twelve24TCToolInternalState}
          handleMethodSettingsChanged={handleMethodSettingsChanged}
        />
        <SelectPlateCode
          twelve24TCInternalState={twelve24TCToolInternalState}
          handleMethodSettingsChanged={handleDestPlateSettingsChanged}
          getAndSetBarcodes={getAndSetBarcodes}
        />
        <PlateBarcodeField
          plateRole={PlatePosition.Dead}
          index={undefined}
          label="Dead Plate Barcode"
          size="small"
          plateBarcode={twelve24TCState.deadPlateBarcode}
          handlePlateBarcodeChange={(index, field, value) =>
            handleDeadPlateBarcodeChange(value)
          }
        />
        <PlateType
          platePosition={PlatePosition.Dead}
          isDisabled={methodSettings!.selectedSystem === 0}
          acceptedPlateTypes={deadPlateTypeInfo}
          plateType={twelve24TCState.deadPlateType}
          setPlateType={(plateTypeInfo) =>
            handleDeadPlateTypeChange(plateTypeInfo.labwareTypeCode)
          }
          isRequired={true}
        />
        <DeadTotalCountSpinOrder
          twelve24TCInternalState={twelve24TCToolInternalState}
          handleMethodSettingsChanged={handleMethodSettingsChanged}
        />
        {twelve24TCState.deadPlateType === "" ? null : (
          <AliquotSettings
            numberOfAliquots={10}
            aliquotVolume={methodSettings.aliquotVolume}
            handleMethodSettingsChanged={handleMethodSettingsChanged}
            is96WellPlate={[
              "PE_96_FL_L_6005182",
              "PE_96_FL_L_6055300",
            ].includes(twelve24TCState.deadPlateType)}
          />
        )}
        <DissociationWashReagent
          twelve24TCInternalState={twelve24TCToolInternalState}
          handleMethodSettingsChanged={handleMethodSettingsChanged}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={methodSettings.dissociation}
              onChange={(e) =>
                handleMethodSettingsChanged("dissociation", e.target.checked)
              }
              name="checkedB"
              color="primary"
              size="small"
            />
          }
          label="Dissociation"
        />
        <NumberField
          size={"small"}
          id="dissociate-time"
          label="Dissociation Time (Minutes)"
          value={methodSettings.dissociationTime!}
          onChange={(value) =>
            handleMethodSettingsChanged("dissociationTime", value)
          }
          minValue={methodSettings.dissociation ? 1 : 0}
          maxValue={60}
          hasError={
            methodSettings.dissociation && !methodSettings.dissociationTime
          }
          errorMessage={
            methodSettings.dissociation && !methodSettings.dissociationTime
              ? "Value can't be undefined"
              : ""
          }
          disabled={!methodSettings.dissociation}
        />
        <DefaultVSpinSettings
          twelve24TCInternalState={twelve24TCToolInternalState}
          handleMethodSettingsChanged={handleMethodSettingsChanged}
        />
        <NumberField
          size={"small"}
          id="spin-gforce"
          label="Spin Params G Force (RCF)"
          value={methodSettings.spinParamGForce}
          onChange={(value) =>
            handleMethodSettingsChanged("spinParamGForce", value)
          }
        />
        <NumberField
          size={"small"}
          id="spin-percentage"
          label="Spin Params (%)"
          value={methodSettings.spinParamPercent}
          onChange={(value) =>
            handleMethodSettingsChanged("spinParamPercent", value)
          }
          minValue={10}
          maxValue={100}
        />
        <NumberField
          size={"small"}
          id="spin-time"
          label="Spin Time (Minutes)"
          value={methodSettings.spinTime}
          onChange={(value) => handleMethodSettingsChanged("spinTime", value)}
          minValue={4}
          maxValue={30}
        />
        <NumberField
          size={"small"}
          id="spin-acceleration"
          label="Acceleration Settings (%)"
          value={methodSettings.spinAccel}
          onChange={(value) => handleMethodSettingsChanged("spinAccel", value)}
          minValue={1}
          maxValue={100}
        />
        <NumberField
          size={"small"}
          id="spin-deceleration"
          label="Deceleration Settings (%)"
          value={methodSettings.spinDecel}
          onChange={(value) => handleMethodSettingsChanged("spinDecel", value)}
          minValue={1}
          maxValue={100}
        />
      </StyledMethodSettingsContentContainer>
    </StyledMethodSettingsContainer>
  );
}
